import React, { useCallback, useEffect, useRef, useState } from "react";
import { ApolloProvider, useApolloClient } from "@apollo/client";

import "./App.css";
import client from "./apolloClient";
import { I18nProvider, LANGUAGES, TranslationKey, useI18n } from "./i18n";
import { useLastUpdated } from "./lastUpdated";
import useStateWithLocalStorage from "./hooks/useStateWithLocalStorage";
import useNearbyStations from "./hooks/useNearbyStations";
import useAutoRefresh from "./hooks/useAutoRefresh";
import useNow from "./hooks/useNow";
import { LocationErrorCode } from "./hooks/useCurrentLocation";
import StationInfo, { STATION_INFO } from "./components/StationInfo";
import { getShareUrl, normalizeStationId } from "./utils/utils";

export const NEARBY_COUNT = 5;
export const REFRESH_INTERVAL_MS = 15 * 1000;
const STALE_AFTER_MS = 60 * 1000;

const LOCATION_ERRORS: Record<LocationErrorCode, TranslationKey> = {
    denied: "locationDenied",
    unavailable: "locationUnavailable",
    timeout: "locationTimeout",
    unsupported: "locationUnsupported",
};

const unique = (ids: Array<string>) => Array.from(new Set(ids));

const Icon = ({ name }: { name: string }) => (
    <span className="material-icons" aria-hidden="true">
        {name}
    </span>
);

// Takes station ids from the ?id= link and removes the parameter, so
// reloading or sharing the page doesn't re-add stations the user removed.
const takeLinkStationIds = () => {
    const url = new URL(window.location.href);
    const idParam = url.searchParams.get("id");
    if (!idParam) return [];
    url.searchParams.delete("id");
    window.history.replaceState(window.history.state, "", url.toString());
    return idParam
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean)
        .map(normalizeStationId);
};

const hasLocationPermission = async () => {
    try {
        const status = await navigator.permissions?.query({
            name: "geolocation",
        });
        return status?.state === "granted";
    } catch {
        return false;
    }
};

const LanguageSwitcher = () => {
    const { language, setLanguage, t } = useI18n();
    return (
        <div className="language" role="group" aria-label={t("language")}>
            {LANGUAGES.map((option) => (
                <button
                    key={option}
                    type="button"
                    lang={option}
                    aria-pressed={language === option}
                    onClick={() => setLanguage(option)}
                >
                    {option.toUpperCase()}
                </button>
            ))}
        </div>
    );
};

const Freshness = ({
    lastUpdated,
    onRefresh,
}: {
    lastUpdated: number;
    onRefresh: () => void;
}) => {
    const { t } = useI18n();
    // Re-render every few seconds so the label keeps counting.
    useNow(5000);
    const age = Math.max(0, Date.now() - lastUpdated);
    const seconds = Math.floor(age / 1000);
    const label =
        seconds < 10
            ? t("updatedJustNow")
            : seconds < 60
            ? t("updatedSeconds", { count: seconds })
            : t("updatedMinutes", { count: Math.floor(seconds / 60) });

    return (
        <button
            type="button"
            className={`freshness ${age > STALE_AFTER_MS ? "freshness--stale" : ""}`}
            onClick={onRefresh}
            title={t("refresh")}
        >
            <Icon name="refresh" />
            <span>{label}</span>
            <span className="visually-hidden">, {t("refresh")}</span>
        </button>
    );
};

export const Stations = () => {
    const { t } = useI18n();
    const apolloClient = useApolloClient();
    const [favourites, setFavourites] = useStateWithLocalStorage<
        Array<string>
    >({ localStorageKey: "favouriteStationIds", defaultValue: [] });
    // "stationIds" holds the latest nearby stations (and, from older versions
    // of the app, stations from ?id= links).
    const [nearby, setNearby] = useStateWithLocalStorage<Array<string>>({
        localStorageKey: "stationIds",
        defaultValue: [],
    });
    const { findNearby, location, locationError, stationsError, searching } =
        useNearbyStations();
    const lastUpdated = useLastUpdated();
    const [notice, setNotice] = useState<string>();

    const favouritesRef = useRef(favourites);
    favouritesRef.current = favourites;

    const refresh = useCallback(() => {
        apolloClient
            .refetchQueries({ include: [STATION_INFO] })
            .catch(() => {});
    }, [apolloClient]);
    useAutoRefresh(refresh, REFRESH_INTERVAL_MS, lastUpdated);

    const searchNearby = useCallback(async () => {
        // Fetch extra stations so there are enough left after hiding the ones
        // that are already favourites.
        const ids = await findNearby(
            NEARBY_COUNT + favouritesRef.current.length
        );
        if (ids) setNearby(ids);
    }, [findNearby, setNearby]);

    const initialized = useRef(false);
    useEffect(() => {
        if (initialized.current) return;
        initialized.current = true;

        const linkIds = takeLinkStationIds();
        const allFavourites = unique([...favouritesRef.current, ...linkIds]);
        if (linkIds.length > 0) setFavourites(allFavourites);

        const nothingSaved = allFavourites.length === 0 && nearby.length === 0;
        if (nothingSaved) {
            searchNearby();
        } else {
            hasLocationPermission().then((granted) => {
                if (granted) searchNearby();
            });
        }
        // Runs once on start-up.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!notice) return;
        const timer = setTimeout(() => setNotice(undefined), 2500);
        return () => clearTimeout(timer);
    }, [notice]);

    const toggleFavourite = (stationId: string) => {
        const id = normalizeStationId(stationId);
        setFavourites(
            favourites.includes(id)
                ? favourites.filter((favourite) => favourite !== id)
                : [...favourites, id]
        );
    };

    const share = async () => {
        const url = getShareUrl(favourites, window.location.href);
        if (navigator.share) {
            try {
                await navigator.share({ title: t("appTitle"), url });
            } catch {
                // Cancelled by the user.
            }
            return;
        }
        try {
            await navigator.clipboard.writeText(url);
            setNotice(t("linkCopied"));
        } catch {
            setNotice(url);
        }
    };

    const nearbyShown = unique(nearby.map(normalizeStationId))
        .filter((id) => !favourites.includes(id))
        .slice(0, NEARBY_COUNT);
    const hasStations = favourites.length > 0 || nearbyShown.length > 0;

    const renderStations = (ids: Array<string>) =>
        ids.map((stationId) => (
            <StationInfo
                key={stationId}
                stationId={stationId}
                location={location}
                isFavourite={favourites.includes(stationId)}
                onToggleFavourite={toggleFavourite}
            />
        ));

    return (
        <div className="app">
            <header className="app-header">
                <h1>{t("appTitle")}</h1>
                <LanguageSwitcher />
            </header>

            <main className="content">
                {hasStations && lastUpdated !== null && (
                    <Freshness lastUpdated={lastUpdated} onRefresh={refresh} />
                )}

                {favourites.length > 0 && (
                    <section aria-labelledby="favourites-heading">
                        <h2 id="favourites-heading">{t("favourites")}</h2>
                        {renderStations(favourites)}
                    </section>
                )}

                {nearbyShown.length > 0 && (
                    <section aria-labelledby="nearby-heading">
                        <h2 id="nearby-heading">{t("nearby")}</h2>
                        {renderStations(nearbyShown)}
                    </section>
                )}

                {locationError && (
                    <p className="alert" role="alert">
                        {t(LOCATION_ERRORS[locationError.code])}
                    </p>
                )}
                {stationsError && (
                    <p className="alert" role="alert">
                        {t("stationsError")}
                    </p>
                )}
                {!hasStations && !searching && (
                    <p className="empty">{t("empty")}</p>
                )}
                <p className="visually-hidden" role="status">
                    {searching ? t("locating") : ""}
                </p>
            </main>

            <footer className="action-bar">
                <button
                    type="button"
                    className="primary-button"
                    onClick={searchNearby}
                    disabled={searching}
                >
                    <Icon name={searching ? "hourglass_empty" : "my_location"} />
                    <span>{searching ? t("locating") : t("findNearby")}</span>
                </button>
                {favourites.length > 0 && (
                    <button
                        type="button"
                        className="icon-button action-bar__share"
                        onClick={share}
                        aria-label={t("share")}
                        title={t("share")}
                    >
                        <Icon name="share" />
                    </button>
                )}
            </footer>

            {notice && (
                <div className="toast" role="status">
                    {notice}
                </div>
            )}
        </div>
    );
};

const App = () => (
    <ApolloProvider client={client}>
        <I18nProvider>
            <Stations />
        </I18nProvider>
    </ApolloProvider>
);

export default App;
